import { type ComponentProps, type ReactNode, useState } from 'react';
import { cn } from '@/shared/lib/cn';
import { formatMoney, parseLocalizedMoney } from '@/shared/lib/money';
import { Input } from '@/shared/ui/primitives/input';

/** Props do MoneyInput: valor em string decimal canônica (ex.: `1234.50`) ou vazio. */
export type MoneyInputProps = Omit<ComponentProps<typeof Input>, 'value' | 'onChange'> & {
  value: string;
  onChange: (value: string) => void;
};

function toDisplay(value: string): string {
  return value === '' ? '' : formatMoney(value).replace(/^(-?)R\$ /, '$1');
}

/**
 * Campo monetário com prefixo R$: aceita `1.234,50`, `1234,5` ou `-10`, mostra pt-BR ao sair
 * do campo e entrega string decimal canônica, sem `number`. Entrada inválida é entregue como
 * digitada para o schema rejeitar.
 */
export function MoneyInput({
  value,
  onChange,
  onBlur,
  className,
  ...props
}: MoneyInputProps): ReactNode {
  const [draft, setDraft] = useState<string | null>(null);
  return (
    <div className="relative">
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-muted-foreground"
      >
        R$
      </span>
      <Input
        inputMode="decimal"
        autoComplete="off"
        {...props}
        className={cn('pl-9 text-right tabular-nums', className)}
        value={draft ?? toDisplay(value)}
        onChange={(event) => {
          const typed = event.target.value.replace(/[^\d.,-]/g, '');
          setDraft(typed);
          onChange(parseLocalizedMoney(typed) ?? typed);
        }}
        onBlur={(event) => {
          setDraft(null);
          onBlur?.(event);
        }}
      />
    </div>
  );
}
